// src/ui/CreatorDashboard.jsx — your numbers, on your own profile.
//
// The phone's twin is src/components/CreatorDashboard.tsx; the maths of what a
// number "says" (deltas, bars, kinds) is services/creatorDash, shared by both.
// Everything comes from the server (insightsRoutes.forAccount) and only to you.
//
//   Overview  views · reach · interactions · posts against the period before;
//             views per day; engagement rate; top posts; when posts do best
//   Content   how each kind of post does; the posts that did best, every number
//   Audience  the people reached, counted once: countries, ages, genders
import React, { useEffect, useMemo, useState } from 'react';
import { fetchInsights, audienceForScreen } from '../services/vibeStats';
import { DASH_RANGES, delta, compact, bars, hourRange, kindRows } from '../services/creatorDash';
import SafeImg from './SafeImg';

const KIND_NAME = { video: 'Videos', photo: 'Photos', text: 'Text', poll: 'Polls', link: 'Links' };
const KIND_GLYPH = { video: '▶', photo: '🖼', text: '✎', poll: '📊', link: '🔗' };

/** The strip at the top of your own profile — the last 28 days. */
export function DashboardStrip({ onOpen }) {
  const [d, setD] = useState(null);
  useEffect(() => {
    let live = true;
    fetchInsights(null, { days: 28 }).then((r) => { if (live && r && r.range) setD(r.range); });
    return () => { live = false; };
  }, []);
  const cell = (label, v, p) => {
    const dl = delta(v, p);
    return (
      <div className="cd-strip-cell">
        <b>{d ? compact(v) : '–'}</b>
        <span>{label}</span>
        {d && dl.pct !== null && dl.dir !== 'flat' && <i className={dl.dir}>{dl.dir === 'up' ? '▲' : '▼'} {Math.abs(dl.pct)}%</i>}
      </div>
    );
  };
  return (
    <button className="cd-strip" onClick={onOpen}>
      <div className="cd-strip-head"><b>Professional dashboard</b><span>Last 28 days ›</span></div>
      <div className="cd-strip-row">
        {cell('Views', d ? d.activity.views : 0, d ? d.activity.prevViews : 0)}
        {cell('Reach', d ? d.activity.reach : 0, d ? d.activity.prevReach : 0)}
        {cell('Interactions', d ? d.current.engagement : 0, d ? d.previous.engagement : 0)}
      </div>
    </button>
  );
}

export default function CreatorDashboard({ onClose, onOpenPost }) {
  const [tab, setTab] = useState('overview');
  const [days, setDays] = useState(28);
  const [data, setData] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let live = true;
    setFailed(false);
    fetchInsights(null, { days }).then((r) => {
      if (!live) return;
      if (r && r.range) setData(r); else setFailed(true);
    });
    return () => { live = false; };
  }, [days]);

  const m = useMemo(() => (data && data.audience ? audienceForScreen(data.audience) : null), [data]);
  const r = data && data.range;

  const Kpi = ({ label, value, prev }) => {
    const dl = delta(value, prev);
    return (
      <div className="cd-kpi">
        <span>{label}</span>
        <b>{compact(value)}</b>
        <i className={dl.dir}>
          {dl.pct === null ? (value > 0 ? 'New' : '—') : `${dl.dir === 'up' ? '▲' : dl.dir === 'down' ? '▼' : '•'} ${Math.abs(dl.pct)}%`}
          <em> vs previous {r ? r.days : days} days</em>
        </i>
      </div>
    );
  };
  const Bar = ({ label, pct, tone }) => (
    <div className="cd-bar">
      <div className="cd-bar-top"><span>{label}</span><b>{pct}%</b></div>
      <div className="cd-bar-track"><div className={`cd-bar-fill ${tone || ''}`} style={{ width: `${Math.max(0, Math.min(100, pct))}%` }} /></div>
    </div>
  );
  const Tile = ({ p }) => (
    <button className="cd-tile" onClick={() => onOpenPost && onOpenPost(p.id)} title={p.text || ''}>
      {p.thumb ? <SafeImg src={p.thumb} kind="inline" loading="lazy" /> : <span className="cd-tile-glyph">{KIND_GLYPH[p.kind] || '✎'}</span>}
      <span className="cd-tile-views">👁 {compact(p.views)}</span>
    </button>
  );

  return (
    <div className="cd">
      <div className="cd-head">
        <button className="cd-back" onClick={onClose} aria-label="Back">‹</button>
        <b>Professional dashboard</b>
      </div>
      <div className="vb-chips">
        {['overview', 'content', 'audience'].map((x) => (
          <button key={x} className={`vb-chip ${tab === x ? 'on' : ''}`} onClick={() => setTab(x)}>{x[0].toUpperCase() + x.slice(1)}</button>
        ))}
      </div>
      {tab !== 'audience' && (
        <div className="vb-chips cd-ranges">
          {DASH_RANGES.map((n) => <button key={n} className={`vb-chip ${days === n ? 'on' : ''}`} onClick={() => setDays(n)}>{n} days</button>)}
        </div>
      )}
      {!data && !failed && <div className="ct-empty subtle"><span>Loading your numbers…</span></div>}
      {!data && failed && <div className="ct-empty subtle"><span>Couldn’t load your numbers — try again in a moment.</span></div>}

      {r && tab === 'overview' && (
        <>
          <div className="cd-kpis">
            <Kpi label="Views" value={r.activity.views} prev={r.activity.prevViews} />
            <Kpi label="Reach" value={r.activity.reach} prev={r.activity.prevReach} />
            <Kpi label="Interactions" value={r.current.engagement} prev={r.previous.engagement} />
            <Kpi label="Posts" value={r.current.posts} prev={r.previous.posts} />
          </div>
          <h4 className="cd-h">Views per day</h4>
          <div className="cd-chart">
            {bars(r.series, 'views').map((h, i) => (
              <div key={i} className={`cd-col ${h > 0 ? 'on' : ''}`} style={{ height: `${Math.max(2, Math.round(h * 100))}%` }} title={`${r.series[i].day}: ${r.series[i].views} views`} />
            ))}
          </div>
          <div className="cd-axis"><span>{r.series[0]?.day}</span><span>{r.series[r.series.length - 1]?.day}</span></div>
          <h4 className="cd-h">Engagement rate</h4>
          <div className="cd-big">{r.current.rate}%<em>likes, comments, shares and saves per person reached</em></div>
          <h4 className="cd-h">Top posts</h4>
          {r.top.length ? <div className="cd-tiles">{r.top.map((p) => <Tile key={p.id} p={p} />)}</div> : <div className="vb-sub">No activity yet — your numbers appear once people see your posts.</div>}
          <div className="cd-note">🕘 {r.bestHour != null ? `Your posts do best when you post around ${hourRange(r.bestHour)}` : 'Post a few more times to learn when your audience is most active'}</div>
        </>
      )}

      {r && tab === 'content' && (
        <>
          <h4 className="cd-h">How each kind of post does <em>· all time</em></h4>
          {kindRows(r.byKind).map((k) => (
            <div className="cd-kind" key={k.kind}>
              <span className="cd-kind-glyph">{KIND_GLYPH[k.kind]}</span>
              <div><b>{KIND_NAME[k.kind]} · {k.posts}</b><i>{compact(k.avgViews)} avg. views · {k.avgEngagement} avg. interactions</i></div>
            </div>
          ))}
          <h4 className="cd-h">Top posts</h4>
          {r.top.map((p) => (
            <button className="cd-postrow" key={p.id} onClick={() => onOpenPost && onOpenPost(p.id)}>
              {p.thumb ? <SafeImg src={p.thumb} kind="inline" loading="lazy" /> : <span className="cd-tile-glyph">{KIND_GLYPH[p.kind]}</span>}
              <div>
                <b>{p.text || KIND_NAME[p.kind]}</b>
                <i>👁 {compact(p.views)} · 👥 {compact(p.reach)} · ❤️ {compact(p.likes)} · 💬 {compact(p.comments)} · ↗ {compact(p.shares)}</i>
              </div>
            </button>
          ))}
        </>
      )}

      {data && tab === 'audience' && (
        <>
          <div className="cd-kpis">
            <Kpi label="People reached" value={data.numbers?.reach || 0} prev={0} />
            <Kpi label="Posts" value={data.numbers?.posts || 0} prev={0} />
          </div>
          {m && m.base > 0 ? (
            <>
              <div className="vb-sub">Based on {m.base} people reached</div>
              <h4 className="cd-h">Top countries</h4>
              {m.countries.map((c, i) => <Bar key={`${c.iso}${i}`} label={`${c.flag} ${c.name}`} pct={c.pct} tone={c.unknown ? 'grey' : ''} />)}
              {m.ageGroups && (<><h4 className="cd-h">Age <em>· of {m.ageBase} people</em></h4>{Object.entries(m.ageGroups).map(([k, v]) => <Bar key={k} label={k} pct={v} />)}</>)}
              {m.gender && (
                <>
                  <h4 className="cd-h">Gender <em>· of {m.genderBase} people</em></h4>
                  <Bar label="Women" pct={m.gender.female} tone="pink" />
                  <Bar label="Men" pct={m.gender.male} />
                  {m.gender.other > 0 && <Bar label="Other" pct={m.gender.other} tone="violet" />}
                </>
              )}
              <div className="vb-sub">IP geolocation by DB-IP</div>
            </>
          ) : <div className="vb-sub">No activity yet — your audience appears once people see your posts.</div>}
        </>
      )}
    </div>
  );
}
